import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../lib/supabaseClient';
import { useT, translateWorkType } from '../lib/i18n';
import { computeSummary } from '../lib/taxService';
import { SignaturePad } from '../components/SignaturePad';
import { notifySigned, notifyRefused } from '../lib/pushService';
import { licenceBadgeText, getLicenceProvider } from '../lib/licenceProvider';
import { generatePDF } from '../lib/pdf';
import { Pencil, CheckCircle2, AlertTriangle, Loader2, Building2, XCircle, MapPin, Phone, Mail, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';

export default function SignPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { t, lang, setLang } = useT();
  const [sub, setSub] = useState(null);
  const [profile, setProfile] = useState(null);
  const [signature, setSignature] = useState(null);
  const [name, setName] = useState('');
  const [state, setState] = useState('loading'); // loading | ready | done | error | already | refused
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [refusalReason, setRefusalReason] = useState('');
  const [downloading, setDownloading] = useState(false);

  // === Light translation helper for PDF labels (matches the t() function in i18n) ===
  // We provide a minimal t-like fn so the PDF respects the current UI language
  // when generated from this public page (where useT().t isn't always in scope
  // for the pdf helpers).
  const pdfT = (key) => {
    const map = lang === 'en' ? {
      'work.type': 'Type of work', 'sum.materials': 'Materials',
      'sum.grand': 'Grand total', 'pdf.from': 'From', 'pdf.to': 'To',
      'pdf.quote': 'Quote', 'pdf.labour': 'Labour', 'pdf.signature': 'Signature',
    } : {
      'work.type': 'Type de travaux', 'sum.materials': 'Matériaux',
      'sum.grand': 'Grand total', 'pdf.from': 'De', 'pdf.to': 'Pour',
      'pdf.quote': 'Soumission', 'pdf.labour': 'Main-d\'œuvre', 'pdf.signature': 'Signature',
    };
    return map[key] || (typeof t === 'function' ? t(key) : key);
  };

  // === Build a Base64 string from the jsPDF instance the same way pdf.js does ===
  // Used by the submit() flow to email a signed copy to the client.
  const buildPdfBase64 = async (subOverride) => {
    const doc = await generatePDF(subOverride || sub, profile || {}, pdfT, { returnDoc: true, lang });
    if (!doc) return null;
    const dataUri = doc.output('datauristring');
    const comma = dataUri.indexOf(',');
    return comma > -1 ? dataUri.slice(comma + 1) : dataUri;
  };

  const handleDownloadPDF = async () => {
    if (!sub) return;
    setDownloading(true);
    try {
      await generatePDF(sub, profile || {}, pdfT, { lang });
    } catch (err) {
      console.warn('PDF download failed', err);
      setError(lang === 'fr' ? 'Échec du téléchargement du PDF' : 'PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  // === Auto-detect the client's preferred language on first visit ===
  // The signature page is opened by an external client (not the contractor)
  // via a magic link. We respect their browser's Accept-Language: if it starts
  // with "en", we switch the UI to English so anglophone clients get a native
  // experience without manually toggling. We only do this once per visit and
  // only if the user hasn't already explicitly chosen a language (no 'lang'
  // entry in localStorage), so we never override an existing preference.
  useEffect(() => {
    try {
      const hasUserChoice = localStorage.getItem('lang');
      if (hasUserChoice) return;
      const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
      if (browserLang.startsWith('en') && lang !== 'en') setLang('en');
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: rows, error: e } = await supabase
          .rpc('get_submission_for_signing', { p_token: token });
        if (cancelled) return;
        const row = Array.isArray(rows) ? rows[0] : rows;
        const data = row?.submission;
        if (e || !data) {
          setError(lang === 'fr' ? 'Lien invalide ou expiré.' : 'Invalid or expired link.');
          setState('error');
          return;
        }
        setSub(data);
        setName(data.client_name || '');
        if (data.signed_at) {
          setState('already');
        } else if (data.status === 'refused') {
          setState('refused');
        } else {
          setState('ready');
        }
        if (!cancelled && row?.profile) setProfile(row.profile);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Error');
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, lang]);

  const submit = async () => {
    if (!signature) {
      setError(lang === 'fr' ? 'Signature requise' : 'Signature required');
      return;
    }
    if (!name.trim()) {
      setError(lang === 'fr' ? 'Nom requis' : 'Name required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { error: e } = await supabase
        .rpc('sign_submission_by_token', { p_token: token, p_signature: signature, p_name: name.trim() });
      if (e) throw e;
      notifySigned({
        submission_id: sub.id,
        user_id: sub.user_id,
        submission_number: sub.submission_number,
        client_name: sub.client_name,
        signed_by_name: name.trim(),
      });
      // === Best-effort: email a signed copy to the client (background) ===
      // We do this AFTER the supabase update succeeds so the signed_at is set,
      // which our public endpoint verifies. We don't await this — the user
      // gets the success screen immediately even if email is slow/fails.
      if (sub.client_email) {
        (async () => {
          try {
            const signedSub = { ...sub, signature_data_url: signature, signed_by_name: name.trim(), signed_at: new Date().toISOString() };
            const b64 = await buildPdfBase64(signedSub);
            if (!b64) return;
            await axios.post(`${API}/email/send-signed-pdf-public`, {
              sign_token: token,
              pdf_base64: b64,
              lang,
              filename: `${sub.submission_number || 'soumission'}-signe.pdf`,
            });
          } catch (e) {
            console.warn('signed-pdf email failed', e);
          }
        })();
      }
      setState('done');
    } catch (err) {
      setError(err.message || 'Failed to sign');
    } finally {
      setSubmitting(false);
    }
  };

  const refuse = async () => {
    if (!name.trim()) {
      setError(lang === 'fr' ? 'Votre nom est requis' : 'Your name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { error: e } = await supabase
        .rpc('refuse_submission_by_token', { p_token: token, p_name: name.trim(), p_reason: (refusalReason || '').trim() });
      if (e) throw e;
      // Notify the contractor that the client refused (best-effort).
      notifyRefused({
        submission_id: sub.id,
        user_id: sub.user_id,
        submission_number: sub.submission_number,
        client_name: sub.client_name,
        refused_by_name: name.trim(),
        refusal_reason: (refusalReason || '').trim(),
      });
      setState('refused');
    } catch (err) {
      setError(err.message || 'Failed to refuse');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#7A1F2B]" size={32} />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <AlertTriangle className="mx-auto mb-4 text-red-600" size={40} />
          <h1 className="font-heading text-2xl font-black mb-2">
            {lang === 'fr' ? 'Lien invalide' : 'Invalid link'}
          </h1>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (state === 'done' || state === 'already') {
    const companyName = profile?.company_name?.trim();
    const clientName = sub?.signed_by_name || sub?.client_name || '';
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="text-emerald-600" size={36} />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
            {lang === 'fr' ? 'Soumission acceptée !' : 'Quote accepted!'}
          </h1>
          <p className="text-gray-600 text-sm">
            {lang === 'fr'
              ? (companyName
                  ? `Merci ${clientName} ! ${companyName} sera notifié.`
                  : `Merci ${clientName} ! Votre soumission a été acceptée.`)
              : (companyName
                  ? `Thank you ${clientName}! ${companyName} will be notified.`
                  : `Thank you ${clientName}! Your quote has been accepted.`)}
          </p>
        </div>
      </div>
    );
  }

  if (state === 'refused') {
    const companyName = profile?.company_name?.trim();
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="text-red-600" size={36} />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
            {lang === 'fr' ? 'Soumission refusée' : 'Quote refused'}
          </h1>
          <p className="text-gray-600 text-sm">
            {lang === 'fr'
              ? (companyName
                  ? `${companyName} a été notifié de votre décision.`
                  : `L'entrepreneur a été notifié de votre décision.`)
              : (companyName
                  ? `${companyName} has been notified of your decision.`
                  : `The contractor has been notified of your decision.`)}
          </p>
        </div>
      </div>
    );
  }

  const summary = sub.summary || computeSummary(sub.materials || [], sub.labour || {}, 0, 0, sub.province, '', lang);

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E5DCC9] pt-safe z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {profile?.logo_url ? (
              <div className="w-9 h-9 rounded-xl bg-[#FAF7F2] border border-[#E5DCC9] overflow-hidden flex items-center justify-center shrink-0">
                <img src={profile.logo_url} alt="" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-9 h-9 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
                <Pencil size={18} strokeWidth={2.4} />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-black truncate">{profile?.company_name || 'SoumissionPro'}</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                {lang === 'fr' ? 'Soumission à signer' : 'Quote to sign'}
              </div>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="h-9 px-3 rounded-lg border-2 border-[#E5DCC9] text-xs font-bold"
          >
            {lang.toUpperCase()}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 space-y-4">
        {/* Carte entreprise — Placée en HAUT et bien visible */}
        <div className="card p-5 bg-[#FAF7F2] border-2 border-[#7A1F2B]" data-testid="sign-from-card">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A1F2B] mb-3 flex items-center gap-1.5">
            <Building2 size={12} strokeWidth={2.5} />
            {lang === 'fr' ? 'Soumission envoyée par' : 'Quote sent by'}
          </div>
          <div className="flex items-start gap-4">
            {profile?.logo_url ? (
              <div className="w-16 h-16 rounded-xl bg-white border border-[#E5DCC9] overflow-hidden flex items-center justify-center shrink-0">
                <img src={profile.logo_url} alt="" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
                <Building2 size={28} strokeWidth={2} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-heading font-black text-xl tracking-tight leading-tight">
                {profile?.company_name || (lang === 'fr' ? 'Nom de l\'entreprise non défini' : 'Company name not set')}
              </div>
              {profile?.rbq_number && (
                <div className="text-xs font-semibold text-gray-700 mt-1">
                  {(() => {
                    const lp = getLicenceProvider(profile?.province);
                    const pref = lp.short === 'Licence' ? 'Licence' : lp.short;
                    return `${pref} ${profile.rbq_number}`;
                  })()}
                </div>
              )}
              <div className="mt-2.5 space-y-1 text-xs text-gray-700">
                {profile?.address && (
                  <div className="flex items-start gap-1.5">
                    <MapPin size={12} className="mt-0.5 text-gray-500 shrink-0" />
                    <span>{profile.address}</span>
                  </div>
                )}
                {profile?.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone size={12} className="text-gray-500 shrink-0" />
                    <span>{profile.phone}</span>
                  </div>
                )}
                {profile?.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail size={12} className="text-gray-500 shrink-0" />
                    <span className="break-all">{profile.email}</span>
                  </div>
                )}
              </div>
              {profile?.rbq_verified && profile?.rbq_number && (
                <div className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-700">
                    <path d="M9 12l2 2 4-4"/>
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                  <span className="text-[10px] font-bold text-emerald-800 tracking-wide">
                    {licenceBadgeText(profile?.province, profile.rbq_number, lang)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
          {sub.submission_number}
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tighter">
          {lang === 'fr' ? 'Soumission pour' : 'Quote for'} {sub.client_name}
        </h1>
        <div className="text-sm text-gray-600">{translateWorkType(sub.work_type, lang)}</div>

        {sub.materials?.length > 0 && (
          <div className="card p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">{t('sum.materials')}</div>
            {sub.materials.map((m, i) => (
              <div key={m.id || `mat-${i}-${m.description || ''}`} className="flex justify-between text-xs gap-3 py-1">
                <span className="flex-1 truncate">{m.description} <span className="text-gray-400">× {m.quantity} {m.unit}</span></span>
                <span className="font-semibold">{money((Number(m.quantity) || 0) * (Number(m.unit_price) || 0))}</span>
              </div>
            ))}
          </div>
        )}

        {summary.labourTotal > 0 && (
          <div className="card p-4" data-testid="sign-labour-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
              {lang === 'fr' ? "Main-d'œuvre" : 'Labour'}
            </div>
            {sub.labour?.uniform !== false ? (
              <div className="flex justify-between text-xs gap-3 py-1">
                <span className="flex-1 truncate">
                  {Number(sub.labour?.workers) || 0} {lang === 'fr' ? 'travailleur(s)' : 'worker(s)'}
                  {' · '}
                  {Number(sub.labour?.hours) || 0} {lang === 'fr' ? 'h' : 'h'}
                  {' × '}
                  {money(Number(sub.labour?.hourly_rate) || 0)}/h
                </span>
                <span className="font-semibold">{money(summary.labourTotal)}</span>
              </div>
            ) : (
              (sub.labour?.individuals || []).map((w, i) => (
                <div key={`lab-${i}`} className="flex justify-between text-xs gap-3 py-1">
                  <span className="flex-1 truncate">
                    {w.name || (lang === 'fr' ? `Travailleur ${i + 1}` : `Worker ${i + 1}`)}
                    {' · '}
                    {Number(w.hours) || 0}h × {money(Number(w.rate) || 0)}/h
                  </span>
                  <span className="font-semibold">{money((Number(w.rate) || 0) * (Number(w.hours) || 0))}</span>
                </div>
              ))
            )}
          </div>
        )}

        {summary.miscFees > 0 && (
          <div className="card p-4" data-testid="sign-misc-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
              {lang === 'fr' ? 'Frais divers' : 'Miscellaneous fees'}
            </div>
            {summary.miscNotes && (
              <div className="text-xs text-gray-700 mb-1.5 italic">{summary.miscNotes}</div>
            )}
            <div className="flex justify-between text-xs gap-3 py-1">
              <span className="flex-1 truncate">{lang === 'fr' ? 'Total des frais' : 'Fees total'}</span>
              <span className="font-semibold">{money(summary.miscFees)}</span>
            </div>
          </div>
        )}

        <div className="card p-4" data-testid="sign-totals-card">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{lang === 'fr' ? 'Matériaux' : 'Materials'}</span>
              <span className="font-semibold">{money(summary.displayMaterials != null ? summary.displayMaterials : summary.materialsTotal)}</span>
            </div>
            {(summary.displayLabour != null ? summary.displayLabour : summary.labourTotal) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{lang === 'fr' ? "Main-d'œuvre" : 'Labour'}</span>
                <span className="font-semibold">{money(summary.displayLabour != null ? summary.displayLabour : summary.labourTotal)}</span>
              </div>
            )}
            {summary.miscFees > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{lang === 'fr' ? 'Frais divers' : 'Misc. fees'}</span>
                <span className="font-semibold">{money(summary.miscFees)}</span>
              </div>
            )}
            <div className="border-t border-[#E5DCC9] my-2"></div>
            <div className="flex justify-between font-bold">
              <span>{lang === 'fr' ? 'Sous-total avant taxes' : 'Subtotal before tax'}</span>
              <span>{money(summary.subtotal)}</span>
            </div>
            {(summary.taxes || []).map((tx, i) => (
              <div key={`tax-${i}-${tx.label}`} className="flex justify-between text-gray-600">
                <span>{tx.label}{tx.rate ? ` (${(tx.rate * 100).toFixed(tx.rate * 100 === Math.round(tx.rate * 100) ? 0 : 3)}%)` : ''}</span>
                <span>{money(tx.amount)}</span>
              </div>
            ))}
            <div className="border-t-2 border-[#7A1F2B] mt-3 pt-3 flex justify-between items-baseline">
              <span className="font-heading font-black text-lg" style={{ color: '#7A1F2B' }}>{t('sum.grand')}</span>
              <span className="font-heading font-black text-2xl tracking-tighter" style={{ color: '#7A1F2B' }} data-testid="sign-grand-total">{money(summary.grandTotal)}</span>
            </div>
          </div>
        </div>

        <div className="card p-4 border-2 border-[#7A1F2B]">
          <div className="font-heading font-black mb-3">
            {lang === 'fr' ? 'Approuver et signer' : 'Approve and sign'}
          </div>

          {/* Pre-signature PDF download — lets the client review/print/share offline */}
          <button
            data-testid="sign-download-pdf"
            type="button"
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="w-full mb-4 h-11 rounded-lg border-2 border-[#C9A961] bg-[#FAF7F2] text-[#7A1F2B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#F3E9D2] transition-colors disabled:opacity-50"
          >
            <Download size={16} strokeWidth={2.5} />
            {downloading
              ? (lang === 'fr' ? 'Préparation…' : 'Preparing…')
              : (lang === 'fr' ? 'Télécharger le PDF avant de signer' : 'Download PDF before signing')}
          </button>

          <label className="label">{lang === 'fr' ? 'Votre nom complet' : 'Your full name'}</label>
          <input
            data-testid="sign-name"
            className="field mb-3"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={lang === 'fr' ? 'Ex: Marc Tremblay' : 'E.g.: Marc Tremblay'}
          />
          <SignaturePad onChange={setSignature} label={lang === 'fr' ? 'Signature' : 'Signature'} />
          {error && <div className="mt-3 text-sm text-red-700 font-bold">{error}</div>}
          <button
            data-testid="sign-submit"
            onClick={submit}
            disabled={submitting || !signature || !name.trim()}
            className="btn-primary w-full mt-4 disabled:opacity-50"
          >
            {submitting
              ? (lang === 'fr' ? 'Envoi…' : 'Submitting…')
              : (lang === 'fr' ? 'Approuver la soumission' : 'Approve quote')}
          </button>
          <p className="text-[10px] text-gray-500 text-center mt-3">
            {lang === 'fr'
              ? 'En signant, vous acceptez les termes de cette soumission.'
              : 'By signing, you accept the terms of this quote.'}
          </p>
        </div>

        {/* NOUVEAU — Section Refuser la soumission */}
        <div className="card p-4 border border-gray-200" data-testid="sign-refuse-card">
          {!refuseOpen ? (
            <button
              data-testid="sign-refuse-open"
              onClick={() => { setRefuseOpen(true); setError(''); }}
              className="w-full h-11 rounded-lg border-2 border-gray-300 text-gray-700 font-bold text-sm hover:border-red-500 hover:text-red-600 transition-colors"
            >
              {lang === 'fr' ? 'Refuser la soumission' : 'Refuse quote'}
            </button>
          ) : (
            <div>
              <div className="font-heading font-black text-red-700 mb-3 flex items-center gap-2">
                <XCircle size={18} />
                {lang === 'fr' ? 'Refuser la soumission' : 'Refuse quote'}
              </div>
              <label className="label">{lang === 'fr' ? 'Votre nom complet' : 'Your full name'}</label>
              <input
                data-testid="refuse-name"
                className="field mb-3"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang === 'fr' ? 'Ex: Marc Tremblay' : 'E.g.: Marc Tremblay'}
              />
              <label className="label">
                {lang === 'fr' ? 'Raison du refus (facultatif)' : 'Reason for refusal (optional)'}
              </label>
              <textarea
                data-testid="refuse-reason"
                className="field mb-3"
                rows={3}
                value={refusalReason}
                onChange={(e) => setRefusalReason(e.target.value)}
                placeholder={lang === 'fr' ? 'Ex: Prix trop élevé, projet annulé...' : 'E.g.: Price too high, project cancelled...'}
              />
              {error && <div className="mb-3 text-sm text-red-700 font-bold">{error}</div>}
              <div className="flex gap-2">
                <button
                  data-testid="refuse-cancel"
                  onClick={() => { setRefuseOpen(false); setError(''); setRefusalReason(''); }}
                  disabled={submitting}
                  className="flex-1 h-11 rounded-lg border-2 border-gray-300 text-gray-700 font-bold text-sm"
                >
                  {lang === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button
                  data-testid="refuse-confirm"
                  onClick={refuse}
                  disabled={submitting || !name.trim()}
                  className="flex-1 h-11 rounded-lg bg-red-600 text-white font-bold text-sm disabled:opacity-50"
                >
                  {submitting
                    ? (lang === 'fr' ? 'Envoi…' : 'Submitting…')
                    : (lang === 'fr' ? 'Confirmer le refus' : 'Confirm refusal')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
